import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { FormikValues } from "formik";
import http from "utilities/http-common";
import { convertFormikToApplication } from "utilities/formik-values-converter";
import { ApplicationState } from "types/applicationState";
import dayjs from "dayjs";
import i18next from "i18next";

const baseUrl = "/api/Applications";
const emptyGuid = "00000000-0000-0000-0000-000000000000";

/**
 * Creates an Empty Application in backend.
 *
 * @returns GUID of the newly created Application.
 */
export async function getEmptyApplicationAsync(): Promise<string> {
    return await http
        .get<string>(`${baseUrl}/create-empty-application`)
        .then((resp: AxiosResponse<string>) => {
            return resp.data;
        })
        .catch((err: AxiosError<Error>) => {
            if (err.code === "ERR_BAD_RESPONSE") {
                console.log("Server responded with a non 200 status code.");
            } else {
                throw err;
            }
            return emptyGuid;
        });
}

export async function getApplicationPackageId(
    applicationId: string,
): Promise<any> {
    return await http
        .get(`${baseUrl}/package/${applicationId}`)
        .then((resp: AxiosResponse<any>) => {
            return resp.data;
        });
}

export interface CompleteApplicationParameters {
    applicationId: string | undefined;
    isDevRequest: boolean;
    values?: FormikValues;
    json?: {};
    completionHash?: string;
}

/**
 * Sends the Formik Values to the backend to create a new Application with the given Application-ID.
 *
 * @param applicationId - GUID of the Application.
 * @param values - Formik Values that are going to be converted to an Application.
 */
export async function completeApplicationAsync({
    applicationId,
    isDevRequest,
    values,
    json,
    completionHash,
}: CompleteApplicationParameters): Promise<void> {
    //* Converts the Formik Values to a proper JSON formatted application.
    var application;
    if (!isDevRequest && values) {
        application = convertFormikToApplication(applicationId, values);
        console.log("APP:");
        console.log(application);
    }

    // console.log(`json: ${json}`);
    // console.log(`jsonConverted: ${JSON.stringify(json, null, 2)}`);
    var url = `${baseUrl}/complete-application`;
    if (completionHash && completionHash != "undefined") {
        url += `?completionHash=${completionHash}`;
    }
    await http
        .post(url, JSON.stringify(isDevRequest ? json : application, null, 2), {
            headers: {
                "Content-Type": "application/json",
            },
        })
        .catch((e) => {
            console.log(e);
        });

    // var responseUrl = await http
    //   .post(
    //     `${baseUrl}/complete-application`,
    //     // JSON.stringify(application, null, 2),
    //     isDevRequest ? json : JSON.stringify(application, null, 2),
    //     {
    //       headers: {
    //         "Content-Type": "application/json",
    //       },
    //     }
    //   )
    //   .then(async (): Promise<string> => {
    //     console.log("Sending document to Sign");
    //     return await createDocumentToSign(applicationId as string);
    //   })
    //   .catch((e) => {
    //     // TODO : implement error handling
    //     // TODO : Remove this console.log
    //     console.log(e);
    //   });

    // if (responseUrl) {
    //   return responseUrl;
    // }
}

export async function finishApplicationAsync(
    applicationId: string,
): Promise<void> {
    await http.post(
        `${baseUrl}/finish-application?applicationId=${applicationId}`,
    );
}

export async function finishApplicationManualAsync(
    applicationId: string,
): Promise<void> {
    await http.post(
        `${baseUrl}/finish-manual-application?applicationId=${applicationId}`,
    );
}

export async function getProcessors(): Promise<Processor[]> {
    return http.get(baseUrl).then((resp: AxiosResponse<Processor[]>) => {
        return resp.data;
    });
}

/**
 * Upload a file to the backend and add link it with a Application.
 *
 * @param formData - Formdate containing the file
 * @param applicationId - GUID of the Application to which the file will be linked with.
 * @param config - Configuration (headers/progress)
 */
export function uploadAttachmentAsync(
    formData: FormData,
    applicationId: string,
    config: AxiosRequestConfig<FormData>,
) {
    return http
        .post(`${baseUrl}/${applicationId}/upload-attachment`, formData, config)
        .then((res: AxiosResponse<GetAttachmentResponse>) => {
            return res.data;
        });
}

export function uploadSingedFile(
    formData: FormData,
    applicationId: string,
    config: AxiosRequestConfig<FormData>,
) {
    return http
        .post(
            `${baseUrl}/${applicationId}/upload-signed-document`,
            formData,
            config,
        )
        .then((res: AxiosResponse<GetAttachmentResponse>) => {
            return res.data;
        });
}

export function uploadAttachmentCertificateAsync(
    formData: FormData,
    certificatenId: string,
    config: AxiosRequestConfig<FormData>,
) {
    return http
        .post(
            `${baseUrl}/${certificatenId}/upload-certificate-attachment`,
            formData,
            config,
        )
        .then((res: AxiosResponse<GetAttachmentResponse>) => {
            return res.data;
        });
}

/**
 * Retrieves a list of Attachments linked to the Application from the backend.
 *
 * @param applicationId - GUID of the Application.
 * @returns All the Attachments that are linked with the Application.
 */

export async function getAttachmentsAsync(
    applicationId: string,
): Promise<GetAttachmentResponse[] | undefined | void> {
    return await http
        .get(`${baseUrl}/${applicationId}/get-files`)
        .then((resp: AxiosResponse<GetAttachmentResponse[]>) => {
            if (resp.data.length > 0) {
                resp.data.map((file) => {
                    file.extension = file.name.split(".").pop() || "";
                    return file;
                });
                // console.log(`attachments:`);
                // console.log(resp.data)
                return resp.data;
            } else {
                return undefined;
            }
        })
        .catch((error: AxiosError<Error>) => {
            if (error.code === "ERR_NETWORK") {
                throw new Error(
                    "Network Error: Could NOT connect to the backend server.",
                );
            }
        });
}

export function getApplications(
    page: number,
    pageSize: number,
    direction: string,
    sortBy: string,
    requester: string,
    processor: string,
    status: string,
    dateFrom: Date | undefined | null,
    referenceCode: string,
    dateSubmission: Date | undefined | null,
    department: string,
    organization: string,
    locations: string,
    lastModificationTime: Date | undefined | null,
    doExact: boolean = false,
) {
    const url = `${baseUrl}/paged`;

    const params = new URLSearchParams();
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("pageSize", pageSize.toString());
    if (direction) params.append("direction", direction);
    if (sortBy) params.append("sortBy", sortBy);
    if (requester) params.append("requester", requester);
    if (processor) params.append("processor", processor);
    if (status) params.append("status", status);
    if (referenceCode) params.append("referenceCode", referenceCode);
    if (department && department !== "---")
        params.append("department", department);
    if (organization) params.append("organization", organization);
    if (locations) params.append("locations", locations);
    if (dateFrom != null) params.append("dateFrom", dateFrom.toString());
    if (dateSubmission != null)
        params.append("creationTime", dateSubmission.toString());
    if (lastModificationTime != null)
        params.append("lastModificationTime", lastModificationTime.toString());
    params.append("doExact", doExact.toString());

    return http
        .get(url, { params: params })
        .then((resp: AxiosResponse<ApplicationsPagedResponse>) => {
            return resp.data;
        });
}

export function updateApplicationProcessor(
    applicationId: string,
    processorId: string,
) {
    var model: UpdateApplicationProcessorModel = {
        applicationId: applicationId,
        processorId: processorId,
    };

    return http
        .put(`${baseUrl}/processor`, model)
        .then((resp: AxiosResponse<ApplicationWithDetails>) => {
            return resp.data;
        });
}

export function updateApplicationStatusSpecific(
    applicationId: string,
    reason: string,
    status: string,
    step?: number,
) {
    var model: UpdateApplicationStatusSpecificModel = {
        applicationId: applicationId,
        reason: reason,
    };

    return http
        .put(`${baseUrl}/status/${status}?step=${step}`, model)
        .then((resp: AxiosResponse<ApplicationWithDetails>) => {
            return resp.data;
        });
}

export function getApplicationByIdAndHash(
    applicationId: string,
    completionHash: string,
) {
    return http
        .get(
            `${baseUrl}/application/${applicationId}?completionHash=${completionHash}`,
        )
        .then((res: AxiosResponse<ApplicationWithDetails>) => {
            return res.data;
        });
}

export function updateCompletionData(
    application: ApplicationWithDetails,
    completionHash: string,
    complete: boolean,
) {
    return http
        .put(
            `${baseUrl}/completion?completionHash=${completionHash}&complete=${complete}`,
            application,
        )
        .then((res: AxiosResponse<ApplicantWithDetails>) => {
            return res.data;
        });
}

export function startCompletionApplication(
    application: ApplicationWithDetails,
): Promise<ApplicationWithDetails> {
    return http
        .post(`${baseUrl}/startup`, application)
        .then((res: AxiosResponse<ApplicationWithDetails>) => {
            return res.data;
        });
}

interface UpdateApplicationProcessorModel {
    applicationId: string;
    processorId: string;
}

interface UpdateApplicationDepartmentModel {
    applicationId: string;
    departmentName: string;
}

interface UpdateApplicationStatusSpecificModel {
    applicationId: string;
    reason: string;
}
// export var applicationsPagedParameters: ApplicationsPagedParameters =
// {
//   page: 1,
//   pageSize: 25,
//   direction: "desc",
//   sortBy: "dateFrom",
//   requester: "",
//   processor: "",
//   status: "",
//   dateFrom: undefined,

// }

// type orderOptions = 'asc' | 'desc';
// export interface ApplicationsPagedParameters {
//   page: number;
//   pageSize: number;
//   direction: orderOptions;
//   sortBy: string;
//   requester: string;
//   processor: string;
//   status: string;
//   dateFrom:Date|undefined;
// }

export function getApplicationWithDetails(id: string) {
    var url = `${baseUrl}/with-details?id=${id}`;

    return http.get(url).then((resp: AxiosResponse<ApplicationWithDetails>) => {
        return resp.data;
    });
}

export async function getApplicationStateAsync(
    applicationId: string,
): Promise<string> {
    return await http
        .get(`${baseUrl}/${applicationId}/state`)
        .then((resp: AxiosResponse<string>) => {
            return resp.data === "" ? ApplicationState.Announced : resp.data;
        });
}

export async function changeApplicationStateAsync(
    applicationId: string,
    state: string,
): Promise<void> {
    await http.post(`${baseUrl}/${applicationId}/change-state`, {
        state: state,
    });
}

export async function exportDataAsCsv(
    dateTo: Date | undefined | null,
    dateFrom: Date | undefined | null,
    creationTimeFrom: Date | undefined | null,
    creationTimeTo: Date | undefined | null,
    extension: string,
) {
    const url = `/api/applications/date-csv-export`;

    const params = new URLSearchParams();
    console.log(dateFrom, dateTo, creationTimeFrom, creationTimeTo);
    if (dateFrom !== null)
        params.append("dateFrom", dayjs(dateFrom).format("YYYY-MM-DD"));
    if (dateTo !== null)
        params.append("dateTo", dayjs(dateTo).format("YYYY-MM-DD"));
    if (creationTimeFrom !== null)
        params.append(
            "creationTimeFrom",
            dayjs(creationTimeFrom).format("YYYY-MM-DD"),
        );
    if (creationTimeTo !== null)
        params.append(
            "creationTimeTo",
            dayjs(creationTimeTo).format("YYYY-MM-DD"),
        );
    if (extension) params.append("extension", extension);

    // Append the i18next language parameter
    const currentLanguage = i18next.language;
    params.append("culture", currentLanguage);

    return await http
        .get(url, { params: params, responseType: "blob" as "json" })
        .then((resp: AxiosResponse<Blob>) => {
            return resp.data;
        });
}

export async function checkPackageStatus(id: string) {
    var url = `${baseUrl}/employee/check-package?id=${id}`;

    return http.get(url).then((resp: AxiosResponse<boolean>) => {
        return resp.data;
    });
}

export function getSignedFile(id: string) {
    var url = `${baseUrl}/employee/get-signed-file?id=${id}`;
    return http
        .get(url, { responseType: "blob" as "json" })
        .then((resp: AxiosResponse<Blob>) => {
            return resp.data;
        });
}

export function getSignedFileInfo(id: string) {
    var url = `${baseUrl}/employee/get-signed-file-info?id=${id}`;
    return http
        .get(url, { responseType: "blob" as "json" })
        .then((resp: AxiosResponse<Blob>) => {
            return resp.data;
        });
}

export async function deleteApplication(applicationId: string): Promise<void> {
    var url = `${baseUrl}/${applicationId}`;

    return http.delete(url).then((resp: AxiosResponse<void>) => {
        return resp.data;
    });
}

export async function checkGuid(applicationdId: string): Promise<boolean> {
    var url = `${baseUrl}/check-guid/${applicationdId}`;

    return http.get(url).then((resp: AxiosResponse<boolean>) => {
        return resp.data;
    });
}

export async function moveApplication(
    applicationId: string,
    tenantId: string,
): Promise<void> {
    var url = `${baseUrl}/move/${applicationId}/${tenantId}`;

    return http.post(url).then((resp: AxiosResponse<void>) => {
        return resp.data;
    });
}
